/**
 * Aerodynamic coefficient tables covering the full −180°…+180° angle-of-attack range.
 *
 * ## Why the full range matters
 *
 * A model that only knows ±20° cannot spin, cannot tumble, and cannot recover — the moment the
 * aeroplane departs, the aero force becomes undefined and the integrator invents something.
 * CLAUDE.md requires the whole circle, blended to a flat plate outside the attached-flow
 * region, so that spins and tumbles *emerge* from the physics instead of being scripted.
 *
 * ## How a table is built
 *
 * Authoring 145 sampled coefficients by hand per surface is unmaintainable and impossible to
 * review. Instead a table is generated from the handful of numbers that are actually published
 * for an aerofoil — lift-curve slope, stall angle, maximum lift, zero-lift drag — and blended
 * into the flat-plate solution outside the linear region:
 *
 *   - **Attached flow** (|α| below the stall): `CL = CLα·(α − α₀)`, `CD = CD₀ + k·CL²`,
 *     the classic parabolic polar.
 *   - **Stall break**: lift falls from `CLmax` toward the flat-plate value over a few degrees.
 *     The sharpness of this drop is what makes a stall *break* rather than mush.
 *   - **Flat plate** (deep stall, past ~30°): `CL = 2·sinα·cosα`, `CD = 2·sin²α`, which is the
 *     standard fully separated result and is what lets the model integrate through a tumble.
 *
 * A config may also supply explicit sampled data, which is used verbatim. Both paths end in the
 * same {@link AeroTable}, so the rest of the FDM cannot tell them apart.
 *
 * Sampling and lookup are allocation-free: the table is three `Float64Array`s over a uniform
 * α grid, so a lookup is an index computation and two lerps.
 */

import { DEG2RAD, PI, TWO_PI, clamp } from '../../core/math.js';

/** Uniform sample spacing across the full circle [deg]. */
const SAMPLE_STEP_DEG = 1;
const SAMPLE_COUNT = 360 / SAMPLE_STEP_DEG + 1; // −180…180 inclusive

/** One surface's coefficients as a function of angle of attack. */
export interface AeroCoefficients {
  /** Lift coefficient, perpendicular to the local flow. */
  cl: number;
  /** Drag coefficient, along the local flow. */
  cd: number;
  /** Pitching-moment coefficient about the surface's aerodynamic centre. */
  cm: number;
}

export function makeAeroCoefficients(): AeroCoefficients {
  return { cl: 0, cd: 0, cm: 0 };
}

/** Parameters that generate a full-circle table from published aerofoil numbers. */
export interface AirfoilSpec {
  /** Lift-curve slope in the attached region [per radian]. Thin-aerofoil theory gives 2π. */
  liftSlopePerRad: number;
  /** Angle of zero lift [deg]. Negative for a cambered aerofoil. */
  zeroLiftAlphaDeg: number;
  /** Positive stall angle [deg]. */
  stallAlphaDeg: number;
  /** Negative stall angle [deg]; typically shallower in magnitude than the positive one. */
  negativeStallAlphaDeg: number;
  /** Maximum lift coefficient, reached at the positive stall angle. */
  clMax: number;
  /** Minimum (most negative) lift coefficient, reached at the negative stall angle. */
  clMin: number;
  /** Profile drag at zero lift. */
  cd0: number;
  /** Induced-drag factor `k` in `CD = CD₀ + k·CL²`, i.e. `1/(π·e·AR)`. */
  inducedDragFactor: number;
  /**
   * How abruptly lift collapses past the stall, in degrees. A small number is a sharp break
   * (and a wing that drops); a large number is the docile mush of a heavily washed-out wing.
   */
  stallSharpnessDeg: number;
  /** Zero-alpha pitching moment about the aerodynamic centre. Negative for camber. */
  cm0: number;
}

/** Explicit sampled data, used verbatim when a config supplies it. */
export interface SampledAeroData {
  alphaDeg: readonly number[];
  cl: readonly number[];
  cd: readonly number[];
  cm: readonly number[];
}

/**
 * A full-circle coefficient table with allocation-free lookup.
 *
 * `updateFixed` / `updateFrame`: neither — a table is immutable once built and is read from
 * the physics tick.
 */
export class AeroTable {
  private readonly clSamples = new Float64Array(SAMPLE_COUNT);
  private readonly cdSamples = new Float64Array(SAMPLE_COUNT);
  private readonly cmSamples = new Float64Array(SAMPLE_COUNT);

  private constructor() {}

  /** Build a table by generating the full circle from published aerofoil parameters. */
  static fromAirfoil(spec: AirfoilSpec): AeroTable {
    const table = new AeroTable();
    for (let i = 0; i < SAMPLE_COUNT; i++) {
      const alphaDeg = -180 + i * SAMPLE_STEP_DEG;
      const alphaRad = alphaDeg * DEG2RAD;
      table.clSamples[i] = generateCl(spec, alphaRad);
      table.cdSamples[i] = generateCd(spec, alphaRad, table.clSamples[i]);
      table.cmSamples[i] = generateCm(spec, alphaRad);
    }
    return table;
  }

  /** Build a table by resampling explicit data onto the uniform grid. */
  static fromSamples(data: SampledAeroData): AeroTable {
    const { alphaDeg, cl, cd, cm } = data;
    if (alphaDeg.length < 2) {
      throw new RangeError('AeroTable.fromSamples: need at least two alpha samples');
    }
    if (alphaDeg.length !== cl.length || alphaDeg.length !== cd.length || alphaDeg.length !== cm.length) {
      throw new RangeError('AeroTable.fromSamples: alphaDeg, cl, cd and cm must be the same length');
    }
    for (let i = 1; i < alphaDeg.length; i++) {
      if (alphaDeg[i] <= alphaDeg[i - 1]) {
        throw new RangeError(
          `AeroTable.fromSamples: alphaDeg must increase strictly (index ${i}: ${alphaDeg[i - 1]} → ${alphaDeg[i]})`,
        );
      }
    }

    const table = new AeroTable();
    for (let i = 0; i < SAMPLE_COUNT; i++) {
      const target = -180 + i * SAMPLE_STEP_DEG;
      table.clSamples[i] = interpolateSeries(alphaDeg, cl, target);
      table.cdSamples[i] = interpolateSeries(alphaDeg, cd, target);
      table.cmSamples[i] = interpolateSeries(alphaDeg, cm, target);
    }
    return table;
  }

  /**
   * Look up coefficients at `alphaRad`, which may be any angle — it is wrapped into the
   * table's range. Writes into `out`; allocation-free.
   */
  sample(alphaRad: number, out: AeroCoefficients): AeroCoefficients {
    // Wrap to (−π, π].
    let alpha = (alphaRad + PI) % TWO_PI;
    if (alpha <= 0) alpha += TWO_PI;
    alpha -= PI;

    const position = (alpha / DEG2RAD + 180) / SAMPLE_STEP_DEG;
    const lowIndex = clamp(Math.floor(position), 0, SAMPLE_COUNT - 2);
    const t = clamp(position - lowIndex, 0, 1);
    const highIndex = lowIndex + 1;

    out.cl = this.clSamples[lowIndex] + (this.clSamples[highIndex] - this.clSamples[lowIndex]) * t;
    out.cd = this.cdSamples[lowIndex] + (this.cdSamples[highIndex] - this.cdSamples[lowIndex]) * t;
    out.cm = this.cmSamples[lowIndex] + (this.cmSamples[highIndex] - this.cmSamples[lowIndex]) * t;
    return out;
  }

  /** Peak lift coefficient anywhere in the table — used by tests and by the stall warning. */
  get clMaxSampled(): number {
    let max = Number.NEGATIVE_INFINITY;
    for (let i = 0; i < SAMPLE_COUNT; i++) if (this.clSamples[i] > max) max = this.clSamples[i];
    return max;
  }

  /**
   * Lift-curve slope through the attached-flow region [1/rad].
   *
   * Measured from the samples rather than read back off the generating spec, so it is also right
   * for a table supplied as explicit −180…180° data — and so it is the slope the panel model
   * actually uses rather than the one it was asked for. A ±4° central difference: wide enough to
   * be clear of sampling quantisation, narrow enough to stay inside the linear region of every
   * surface here (the softest stall in the aircraft is the fuselage's at 35°).
   */
  get liftSlopePerRad(): number {
    const step = 4 * DEG2RAD;
    return (this.sampleCl(step) - this.sampleCl(-step)) / (2 * step);
  }

  /** Lift coefficient alone, for callers that do not want to build a coefficient struct. */
  private sampleCl(alphaRad: number): number {
    const scratch = { cl: 0, cd: 0, cm: 0 };
    this.sample(alphaRad, scratch);
    return scratch.cl;
  }

  /** Angle of attack at which lift peaks [rad]. */
  get stallAlphaRad(): number {
    let max = Number.NEGATIVE_INFINITY;
    let index = 0;
    for (let i = 0; i < SAMPLE_COUNT; i++) {
      if (this.clSamples[i] > max) {
        max = this.clSamples[i];
        index = i;
      }
    }
    return (-180 + index * SAMPLE_STEP_DEG) * DEG2RAD;
  }
}

/** Flat-plate lift: fully separated flow. Peaks at 45°. */
export function flatPlateCl(alphaRad: number): number {
  return 2 * Math.sin(alphaRad) * Math.cos(alphaRad);
}

/** Flat-plate drag: zero edge-on, maximum broadside. */
export function flatPlateCd(alphaRad: number): number {
  const s = Math.sin(alphaRad);
  return 2 * s * s;
}

function generateCl(spec: AirfoilSpec, alphaRad: number): number {
  const alphaDeg = alphaRad / DEG2RAD;
  const zeroLift = spec.zeroLiftAlphaDeg;
  const positiveStall = spec.stallAlphaDeg;
  const negativeStall = spec.negativeStallAlphaDeg;

  // Attached flow: the linear region between the two stall angles.
  if (alphaDeg <= positiveStall && alphaDeg >= negativeStall) {
    const linear = spec.liftSlopePerRad * (alphaRad - zeroLift * DEG2RAD);
    // Clamp to the published extremes so a steep slope cannot overshoot CLmax at the corner.
    return clamp(linear, spec.clMin, spec.clMax);
  }

  // Past the stall, fall from the peak toward the flat plate over `stallSharpnessDeg`.
  const stallEdgeDeg = alphaDeg > 0 ? positiveStall : negativeStall;
  const peak = alphaDeg > 0 ? spec.clMax : spec.clMin;
  const distanceDeg = Math.abs(alphaDeg - stallEdgeDeg);
  const blend = clamp(distanceDeg / Math.max(1e-3, spec.stallSharpnessDeg), 0, 1);
  const plate = flatPlateCl(alphaRad);

  // Smoothstep so the blend has no kink where it meets either end.
  const s = blend * blend * (3 - 2 * blend);

  // Beyond ±90° the "peak" anchor is meaningless — the surface is flying backwards — so the
  // flat plate takes over entirely.
  if (Math.abs(alphaDeg) > 90) return plate;
  return peak * (1 - s) + plate * s;
}

function generateCd(spec: AirfoilSpec, alphaRad: number, cl: number): number {
  const alphaDeg = Math.abs(alphaRad / DEG2RAD);
  const attachedLimit = Math.max(Math.abs(spec.stallAlphaDeg), Math.abs(spec.negativeStallAlphaDeg));

  const parabolic = spec.cd0 + spec.inducedDragFactor * cl * cl;
  const plate = flatPlateCd(alphaRad) + spec.cd0 * Math.abs(Math.cos(alphaRad));

  if (alphaDeg <= attachedLimit) return parabolic;

  // Separated flow adds drag fast; blend over twice the stall sharpness because drag rises
  // more gradually than lift collapses.
  const blend = clamp(
    (alphaDeg - attachedLimit) / Math.max(1e-3, spec.stallSharpnessDeg * 2),
    0,
    1,
  );
  const s = blend * blend * (3 - 2 * blend);
  return parabolic * (1 - s) + plate * s;
}

function generateCm(spec: AirfoilSpec, alphaRad: number): number {
  // About the aerodynamic centre the moment is nearly constant in attached flow. Once
  // separated, the centre of pressure sweeps aft, which is what produces the strong nose-down
  // pitch of a deep stall and the pitch oscillation of a tumble.
  const separated = -0.5 * Math.sin(2 * alphaRad);
  const alphaDeg = Math.abs(alphaRad / DEG2RAD);
  const attachedLimit = Math.max(Math.abs(spec.stallAlphaDeg), Math.abs(spec.negativeStallAlphaDeg));
  if (alphaDeg <= attachedLimit) return spec.cm0;

  const blend = clamp((alphaDeg - attachedLimit) / Math.max(1e-3, spec.stallSharpnessDeg * 2), 0, 1);
  const s = blend * blend * (3 - 2 * blend);
  return spec.cm0 * (1 - s) + separated * s;
}

/** Linear interpolation over an irregular series, holding the end values outside the range. */
function interpolateSeries(
  xs: readonly number[],
  ys: readonly number[],
  x: number,
): number {
  if (x <= xs[0]) return ys[0];
  const last = xs.length - 1;
  if (x >= xs[last]) return ys[last];

  let high = 1;
  while (high < last && xs[high] < x) high++;
  const low = high - 1;
  const t = (x - xs[low]) / (xs[high] - xs[low]);
  return ys[low] + (ys[high] - ys[low]) * t;
}

/**
 * A one-dimensional lookup over an irregular grid — propeller coefficient curves, engine power
 * curves, thrust tables. Allocation-free after construction.
 */
export class Curve1D {
  private readonly xs: Float64Array;
  private readonly ys: Float64Array;

  constructor(xs: readonly number[], ys: readonly number[], name = 'curve') {
    if (xs.length < 2) throw new RangeError(`${name}: need at least two points`);
    if (xs.length !== ys.length) throw new RangeError(`${name}: x and y must be the same length`);
    for (let i = 1; i < xs.length; i++) {
      if (xs[i] <= xs[i - 1]) {
        throw new RangeError(`${name}: x must increase strictly (index ${i}: ${xs[i - 1]} → ${xs[i]})`);
      }
    }
    this.xs = Float64Array.from(xs);
    this.ys = Float64Array.from(ys);
  }

  /** Interpolate, holding the end values outside the defined range. */
  at(x: number): number {
    const xs = this.xs;
    const last = xs.length - 1;
    if (x <= xs[0]) return this.ys[0];
    if (x >= xs[last]) return this.ys[last];

    let high = 1;
    while (high < last && xs[high] < x) high++;
    const low = high - 1;
    const t = (x - xs[low]) / (xs[high] - xs[low]);
    return this.ys[low] + (this.ys[high] - this.ys[low]) * t;
  }

  get minX(): number {
    return this.xs[0];
  }

  get maxX(): number {
    return this.xs[this.xs.length - 1];
  }
}
